import type { Accessor, Component, ComponentProps, JSX, ParentProps, Setter, ValidComponent } from "solid-js";
import { Show, createContext, createSignal, createUniqueId, splitProps, useContext } from "solid-js";

import type * as DialogPrimitive from "@kobalte/core/dialog";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";

import { createDisclosureState } from "@kobalte/core";
import createPresence from "solid-presence";
import { useWidgetApp } from "~/provider";
import { cn } from "~/util/utils";
import { CloseButton } from "./close";

interface DialogContextOptions extends ParentProps {
	withCloseButton?: boolean;

	// Whether the dialog is open or closed
	open: Accessor<boolean>;
	close: () => void;

	// Unique IDs for the title and description
	titleId: string;
	descriptionId: string;

	// DOM references for the overlay and content
	overlayRef?: Accessor<HTMLElement | undefined>;
	contentRef?: Accessor<HTMLElement | undefined>;
	setOverlayRef?: Setter<HTMLElement | undefined>;
	setContentRef?: Setter<HTMLElement | undefined>;

	// Presence state for the overlay and content
	overlayPresent: Accessor<boolean>;
	contentPresent: Accessor<boolean>;
}

interface DialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	withCloseButton?: boolean;
}

const DialogContext = createContext<DialogContextOptions>();
const useDialogContext = () => {
	const context = useContext(DialogContext);

	if (!context) {
		throw new Error("Dialog compound components must be rendered within the Dialog component");
	}

	return context;
};

const Dialog = (props: ParentProps<DialogProps>) => {
	const { parent } = useWidgetApp();

	const [contentRef, setContentRef] = createSignal<HTMLElement | null>(null);
	const [overlayRef, setOverlayRef] = createSignal<HTMLElement | null>(null);

	const disclosure = createDisclosureState({
		open: () => props.open,
		onOpenChange: (isOpen) => {
			props.onOpenChange(isOpen);

			if (isOpen) {
				Object.assign(parent.body.style, { overflow: "hidden", "pointer-events": "none" });
			} else {
				Object.assign(parent.body.style, { overflow: "", "pointer-events": "" });

				if (!parent.body.getAttribute("style")) {
					parent.body.removeAttribute("style");
				}
			}
		},
	});

	const { present: contentPresent } = createPresence({
		show: disclosure.isOpen,
		element: contentRef,
	});

	const { present: overlayPresent } = createPresence({
		show: disclosure.isOpen,
		element: overlayRef,
	});

	const providerValue = {
		open: disclosure.isOpen,
		close: disclosure.close,
		withCloseButton: props.withCloseButton,
		titleId: createUniqueId(),
		descriptionId: createUniqueId(),
		contentRef,
		overlayRef,
		setContentRef,
		setOverlayRef,
		contentPresent,
		overlayPresent,
	} as DialogContextOptions;

	return <DialogContext.Provider value={providerValue}>{props.children}</DialogContext.Provider>;
};

const DialogPortal = (props: JSX.HTMLAttributes<HTMLDivElement>) => {
	return <div {...props} />;
};

interface DialogOverlayProps<T extends ValidComponent = "div"> extends DialogPrimitive.DialogOverlayProps<T> {
	class?: string | undefined;
}

const DialogOverlay = <T extends ValidComponent = "div">(props: PolymorphicProps<T, DialogOverlayProps<T>>) => {
	const [local, others] = splitProps(props as DialogOverlayProps, ["class"]);
	const { open, close, overlayPresent, setOverlayRef } = useDialogContext();

	return (
		<Show when={overlayPresent()}>
			<div
				ref={setOverlayRef}
				class={cn(
					"fixed inset-0 z-[1000] bg-slate-950/30 backdrop-blur-sm",
					"data-[expanded]:animate-in data-[expanded]:fade-in-0",
					"data-[closed]:animate-out data-[closed]:fade-out-0",
					local.class,
				)}
				data-expanded={open() ? "true" : undefined}
				data-closed={!open() ? "true" : undefined}
				onClick={close}
				{...others}
			/>
		</Show>
	);
};

interface DialogContentProps<T extends ValidComponent = "div"> extends DialogPrimitive.DialogContentProps<T> {
	class?: string | undefined;
	children?: JSX.Element;
	closeButtonClass?: string;
}

const DialogContent = (props: DialogContentProps) => {
	const [local, rest] = splitProps(props, ["class", "children"]);

	const { titleId, descriptionId, withCloseButton, open, close, contentPresent, setContentRef } = useDialogContext();

	return (
		<Show when={contentPresent()}>
			<DialogPortal>
				<DialogOverlay />
				<div
					ref={setContentRef}
					class={cn(
						"fixed left-1/2 top-1/2 z-[1000] grid max-h-screen w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 rounded-lg",
						"data-[expanded]:animate-in data-[expanded]:fade-in-0 data-[expanded]:zoom-in-95 data-[expanded]:slide-in-from-left-1/2 data-[expanded]:slide-in-from-top-[48%]",
						"data-[closed]:animate-out data-[closed]:fade-out-0 data-[closed]:zoom-out-95 data-[closed]:slide-out-to-left-1/2 data-[closed]:slide-out-to-top-[48%]",
						local.class,
					)}
					role="alertdialog"
					aria-describedby={`yaa-dialog-description-${descriptionId}`}
					aria-labelledby={`yaa-dialog-title-${titleId}`}
					data-expanded={open() ? "" : undefined}
					data-closed={!open() ? "" : undefined}
					{...rest}
				>
					{local.children}
					<Show when={withCloseButton === true || withCloseButton === undefined}>
						<CloseButton
							class={props.closeButtonClass}
							data-state={open() ? "open" : "closed"}
							onClick={close}
						/>
					</Show>
				</div>
			</DialogPortal>
		</Show>
	);
};

const DialogHeader: Component<ComponentProps<"div">> = (props) => {
	const [, rest] = splitProps(props, ["class"]);

	return (
		<div
			class={cn("flex flex-col space-y-1.5 text-center sm:text-left", props.class)}
			{...rest}
		/>
	);
};

const DialogFooter: Component<ComponentProps<"div">> = (props) => {
	const [, rest] = splitProps(props, ["class"]);
	return (
		<div
			class={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", props.class)}
			role="group"
			{...rest}
		/>
	);
};

interface DialogTitleProps extends JSX.HTMLAttributes<HTMLHeadingElement> {}

const DialogTitle = (props: DialogTitleProps) => {
	const [, rest] = splitProps(props as DialogTitleProps, ["class"]);
	const { titleId } = useDialogContext();

	return (
		<h2
			id={`yaa-dialog-title-${titleId}`}
			class={cn("text-lg font-semibold leading-none tracking-tight", props.class)}
			{...rest}
		/>
	);
};

interface DialogDescriptionProps extends JSX.HTMLAttributes<HTMLParagraphElement> {
	class?: string | undefined;
}

const DialogDescription = (props: DialogDescriptionProps) => {
	const [, rest] = splitProps(props as DialogDescriptionProps, ["class"]);
	const { descriptionId } = useDialogContext();

	return (
		<p
			id={`yaa-dialog-description-${descriptionId}`}
			class={cn("text-sm text-muted-foreground", props.class)}
			{...rest}
		/>
	);
};

export { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription };
